import { useMemo } from "react";
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import type { BucketSize, PeriodSuccessRate } from "@/lib/analytics/types";
import { bucketAdverb } from "@/lib/analytics/buckets";

interface SuccessRateTrendProps {
  rows: PeriodSuccessRate[];
  bucket: BucketSize;
  /** Period B rows (shifted to align index-wise with period A). */
  compareRows?: PeriodSuccessRate[];
}

export function SuccessRateTrend({ rows, bucket, compareRows }: SuccessRateTrendProps) {
  const hasCompare = compareRows != null && compareRows.length > 0;

  // Merge current + compare into a single dataset aligned by index.
  const data = useMemo(() => {
    return rows.map((r, i) => {
      const entry: Record<string, number | string> = {
        period: r.period,
        pct: Math.round(r.rate * 100),
      };
      if (compareRows) {
        const cmp = compareRows[i];
        entry["pctCompare"] = cmp ? Math.round(cmp.rate * 100) : 0;
        entry["_comparePeriod"] = cmp?.period ?? "";
      }
      return entry;
    });
  }, [rows, compareRows]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Success rate trend</CardTitle>
        <CardDescription>
          % of finished runs that succeeded, {bucketAdverb(bucket)}
          {hasCompare ? " — dotted = previous period" : ""}
        </CardDescription>
      </CardHeader>
      <CardContent className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 12, right: 12, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
            <XAxis dataKey="period" stroke="var(--muted-foreground)" fontSize={12} />
            <YAxis
              domain={[0, 100]}
              tickFormatter={(v) => `${v}%`}
              stroke="var(--muted-foreground)"
              fontSize={12}
            />
            <Tooltip
              contentStyle={{
                background: "var(--popover)",
                border: "1px solid var(--border)",
                borderRadius: "0.375rem",
                fontSize: "0.75rem",
              }}
              formatter={(v, name, props) => {
                if (name === "pctCompare") {
                  const cmpPeriod = (props.payload as Record<string, unknown>)["_comparePeriod"];
                  const label = cmpPeriod ? `Success % prev (${String(cmpPeriod)})` : "Success % prev";
                  return [`${v}%`, label];
                }
                return [`${v}%`, "Success rate"];
              }}
            />
            <Line
              type="monotone"
              dataKey="pct"
              stroke="var(--chart-2)"
              strokeWidth={2}
              dot={{ r: 3 }}
              name="Success rate"
              isAnimationActive={false}
            />
            {hasCompare ? (
              <Line
                type="monotone"
                dataKey="pctCompare"
                stroke="var(--chart-2)"
                strokeWidth={2}
                strokeDasharray="4 2"
                strokeOpacity={0.6}
                dot={false}
                name="pctCompare"
                isAnimationActive={false}
              />
            ) : null}
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
